/**
 * Companies, pipelines and deals, as TanStack Query hooks.
 *
 * Kept beside queries.ts rather than in it so the CRM can grow without that
 * file growing with it. Same rules: every type comes from the generated
 * schema, every write invalidates what it changed, and nothing keeps its own
 * copy of server state.
 *
 * A deal moving stage changes the board's cards, its column totals, the
 * deal's own page and its company's totals, so a move invalidates all four.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from './client';
import type { components, paths } from './schema';

type S = components['schemas'];
export type CompanySummary = S['CompanySummary'];
export type CompanyDetail = S['CompanyDetail'];
export type CompanyRole = S['CompanyRole'];
export type CompanyRoleKey = S['CompanyRoleKey'];
export type CompanyRoleActivity = S['CompanyRoleActivity'];
export type BuyerRoleActivity = S['BuyerRoleActivity'];
export type SupplierRoleActivity = S['SupplierRoleActivity'];
export type RoleFigures = S['RoleFigures'];
export type Pipeline = S['Pipeline'];
export type PipelineStage = S['PipelineStage'];
export type StageKind = S['StageKind'];
export type DealSummary = S['DealSummary'];
export type DealDetail = S['DealDetail'];
export type DealStatus = S['DealStatus'];
export type DealsSummary = S['DealsSummaryResponse'];
export type StageTotals = S['StageTotals'];

export type CompanyListParams = NonNullable<paths['/api/v1/companies']['get']['parameters']['query']>;
export type DealListParams = NonNullable<paths['/api/v1/deals']['get']['parameters']['query']>;
export type DealSummaryParams = NonNullable<paths['/api/v1/deals/summary']['get']['parameters']['query']>;

/** Drop empty strings, so a cleared filter is not sent as `?q=`. */
function clean<T extends Record<string, unknown>>(params: T): T {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== '' && v !== undefined && v !== null),
  ) as T;
}

/** A 204 has no body, so unwrap() would read it as empty: check the status instead. */
function noContent(res: { error?: unknown; response: Response }, what: string) {
  if (res.error !== undefined) {
    const e = res.error as { detail?: string };
    throw new Error(e.detail ?? `${what} failed`);
  }
}

// ---------------------------------------------------------------------------
// Companies
// ---------------------------------------------------------------------------

export function useCompanies(params: CompanyListParams, enabled = true) {
  const query = clean(params);
  return useQuery({
    queryKey: ['companies', query],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/companies', { params: { query } }), 'CompanyListResponse'),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useCompany(id: string | null) {
  return useQuery({
    queryKey: ['company', id],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/companies/{id}', { params: { path: { id: id! } } }), 'CompanyDetail'),
    enabled: !!id,
  });
}

function useInvalidateCrm() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['companies'] });
    qc.invalidateQueries({ queryKey: ['company'] });
    qc.invalidateQueries({ queryKey: ['deals'] });
    qc.invalidateQueries({ queryKey: ['deal'] });
    qc.invalidateQueries({ queryKey: ['deals-summary'] });
  };
}

export function useCreateCompany() {
  const invalidate = useInvalidateCrm();
  return useMutation({
    mutationFn: async (body: S['CreateCompanyBody']) =>
      unwrap(await api.POST('/api/v1/companies', { body }), 'CompanyDetail'),
    onSuccess: invalidate,
  });
}

export function useUpdateCompany() {
  const invalidate = useInvalidateCrm();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: S['UpdateCompanyBody'] }) =>
      unwrap(await api.PATCH('/api/v1/companies/{id}', { params: { path: { id } }, body }), 'CompanyDetail'),
    onSuccess: invalidate,
  });
}

export function useDeleteCompany() {
  const invalidate = useInvalidateCrm();
  return useMutation({
    mutationFn: async (id: string) => {
      noContent(await api.DELETE('/api/v1/companies/{id}', { params: { path: { id } } }), 'Delete');
      return id;
    },
    onSuccess: invalidate,
  });
}

export function useMergeCompany() {
  const invalidate = useInvalidateCrm();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, otherId }: { id: string; otherId: string }) =>
      unwrap(
        await api.POST('/api/v1/companies/{id}/merge', {
          params: { path: { id } },
          body: { other_company_id: otherId },
        }),
        'CompanyMergeResult',
      ),
    onSuccess: () => {
      invalidate();
      // The partner and source lists show each record's company.
      qc.invalidateQueries({ queryKey: ['entities'] });
    },
  });
}

// ---------------------------------------------------------------------------
// A company's roles: data buyer and data seller
// ---------------------------------------------------------------------------

/** A company's buyer and supplier records at work: routing, figures, what uses them. */
export function useCompanyRoles(id: string | null, enabled = true) {
  return useQuery({
    queryKey: ['company-roles', id],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/companies/{id}/roles', { params: { path: { id: id! } } }), 'CompanyRoleActivity'),
    enabled: !!id && enabled,
    staleTime: 30 * 1000,
  });
}

function useInvalidateRoles() {
  const invalidate = useInvalidateCrm();
  const qc = useQueryClient();
  return () => {
    invalidate();
    qc.invalidateQueries({ queryKey: ['company-roles'] });
    // The buyer and supplier records are the routing pages' rows too.
    qc.invalidateQueries({ queryKey: ['entities'] });
  };
}

/** Make a company a data buyer or a data seller: creates the record behind the role. */
export function useAddCompanyRole() {
  const invalidate = useInvalidateRoles();
  return useMutation({
    mutationFn: async ({ id, role }: { id: string; role: CompanyRoleKey }) =>
      unwrap(
        await api.POST('/api/v1/companies/{id}/roles', { params: { path: { id } }, body: { role } }),
        'CompanyDetail',
      ),
    onSuccess: invalidate,
  });
}

/** Take a role away: refused, with the reason, while anything uses its record. */
export function useRemoveCompanyRole() {
  const invalidate = useInvalidateRoles();
  return useMutation({
    mutationFn: async ({ id, role }: { id: string; role: CompanyRoleKey }) =>
      unwrap(
        await api.DELETE('/api/v1/companies/{id}/roles/{role}', { params: { path: { id, role } } }),
        'CompanyDetail',
      ),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------------------
// Pipelines
// ---------------------------------------------------------------------------

export function usePipelines(enabled = true) {
  return useQuery({
    queryKey: ['pipelines'],
    queryFn: async () => unwrap(await api.GET('/api/v1/pipelines', {}), 'PipelineListResponse'),
    staleTime: 60 * 1000,
    enabled,
  });
}

/** Every pipeline and stage write, each returning the pipeline as it now is. */
export function usePipelineEdits() {
  const qc = useQueryClient();
  const done = () => {
    qc.invalidateQueries({ queryKey: ['pipelines'] });
    qc.invalidateQueries({ queryKey: ['deals-summary'] });
    qc.invalidateQueries({ queryKey: ['deals'] });
  };
  const createPipeline = useMutation({
    mutationFn: async (body: S['CreatePipelineBody']) =>
      unwrap(await api.POST('/api/v1/pipelines', { body }), 'Pipeline'),
    onSuccess: done,
  });
  const updatePipeline = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: S['UpdatePipelineBody'] }) =>
      unwrap(await api.PATCH('/api/v1/pipelines/{id}', { params: { path: { id } }, body }), 'Pipeline'),
    onSuccess: done,
  });
  const createStage = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: S['CreateStageBody'] }) =>
      unwrap(await api.POST('/api/v1/pipelines/{id}/stages', { params: { path: { id } }, body }), 'Pipeline'),
    onSuccess: done,
  });
  const updateStage = useMutation({
    mutationFn: async ({ id, stageId, body }: { id: string; stageId: string; body: S['UpdateStageBody'] }) =>
      unwrap(
        await api.PATCH('/api/v1/pipelines/{id}/stages/{stage_id}', {
          params: { path: { id, stage_id: stageId } },
          body,
        }),
        'Pipeline',
      ),
    onSuccess: done,
  });
  const deleteStage = useMutation({
    mutationFn: async ({ id, stageId }: { id: string; stageId: string }) =>
      unwrap(
        await api.DELETE('/api/v1/pipelines/{id}/stages/{stage_id}', {
          params: { path: { id, stage_id: stageId } },
        }),
        'Pipeline',
      ),
    onSuccess: done,
  });
  const reorderStages = useMutation({
    mutationFn: async ({ id, stageIds }: { id: string; stageIds: string[] }) =>
      unwrap(
        await api.PUT('/api/v1/pipelines/{id}/stages/order', {
          params: { path: { id } },
          body: { stage_ids: stageIds },
        }),
        'Pipeline',
      ),
    onSuccess: done,
  });
  return { createPipeline, updatePipeline, createStage, updateStage, deleteStage, reorderStages };
}

// ---------------------------------------------------------------------------
// Deals
// ---------------------------------------------------------------------------

export function useDeals(params: DealListParams, enabled = true) {
  const query = clean(params);
  return useQuery({
    queryKey: ['deals', query],
    queryFn: async () => unwrap(await api.GET('/api/v1/deals', { params: { query } }), 'DealListResponse'),
    staleTime: 15 * 1000,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useDealsSummary(params: DealSummaryParams, enabled = true) {
  const query = clean(params);
  return useQuery({
    queryKey: ['deals-summary', query],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/deals/summary', { params: { query } }), 'DealsSummaryResponse'),
    staleTime: 15 * 1000,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useDeal(id: string | null) {
  return useQuery({
    queryKey: ['deal', id],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/deals/{id}', { params: { path: { id: id! } } }), 'DealDetail'),
    enabled: !!id,
  });
}

export function useCreateDeal() {
  const invalidate = useInvalidateCrm();
  return useMutation({
    mutationFn: async (body: S['CreateDealBody']) =>
      unwrap(await api.POST('/api/v1/deals', { body }), 'DealDetail'),
    onSuccess: invalidate,
  });
}

export function useUpdateDeal() {
  const invalidate = useInvalidateCrm();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: S['UpdateDealBody'] }) =>
      unwrap(await api.PATCH('/api/v1/deals/{id}', { params: { path: { id } }, body }), 'DealDetail'),
    onSuccess: invalidate,
  });
}

/** Move a deal to a stage. The server writes the history and sets the status. */
export function useMoveDeal() {
  const invalidate = useInvalidateCrm();
  return useMutation({
    mutationFn: async ({ id, stageId, reason }: { id: string; stageId: string; reason?: string }) =>
      unwrap(
        await api.POST('/api/v1/deals/{id}/stage', {
          params: { path: { id } },
          body: { stage_id: stageId, ...(reason ? { reason } : {}) },
        }),
        'DealDetail',
      ),
    onSuccess: invalidate,
  });
}

export function useDeleteDeal() {
  const invalidate = useInvalidateCrm();
  return useMutation({
    mutationFn: async (id: string) => {
      noContent(await api.DELETE('/api/v1/deals/{id}', { params: { path: { id } } }), 'Delete');
      return id;
    },
    onSuccess: invalidate,
  });
}
